import React, { useState } from "react";
import { Eye, EyeOff, Check, X, Shield, Lock, ShieldCheck } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/api";
import ToastMessage from "../../utils/mui/righttoptoast";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const practiseid = window.getPracticeId();

  // States
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [buttonText, setButtonText] = useState("Set Password");
  const [alertmessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [open, setOpen] = useState(false);

  // Toast close handler
  const handlealert = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // Validation rules
  const hasMinLength = newPassword.length >= 6;
  const hasNumber = /\d/.test(newPassword);
  const hasUppercase = /[A-Z]/.test(newPassword);
  const hasLowercase = /[a-z]/.test(newPassword);
  const hasSpecial = /[!@#$%^&*()]/.test(newPassword);
  const passwordsMatch =
    newPassword === confirmPassword &&
    newPassword !== "" &&
    confirmPassword !== "";

  const requirements = [
    { met: hasMinLength, text: "At least 6 characters" },
    { met: hasNumber, text: "One number (1-9)" },
    { met: hasUppercase, text: "One uppercase letter" },
    { met: hasLowercase, text: "One lowercase letter" },
    { met: hasSpecial, text: "One special character (!@#$%^&*())" },
    { met: passwordsMatch, text: "Passwords match" },
  ];

  const metCount = requirements.filter((r) => r.met).length;
  const progressPercentage = (metCount / requirements.length) * 100;
  const allRequirementsMet = requirements.every((r) => r.met);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setAlertMessage("Invalid or missing reset token.");
      setSeverity("error");
      setOpen(true);
      return;
    }

    setButtonText("Submitting...");

    try {
      const response = await api.post(`/v1/${practiseid}/auth/reset-password`, {
        token: token,
        password: newPassword,
      });

      if (response.status === 200) {
        setAlertMessage("Password reset was successful!");
        setSeverity("success");
        setOpen(true);
        setTimeout(() => navigate("/login"), 2000);
      } else if (response.status === 201) {
        setAlertMessage(response.message || "Password updated with warnings.");
        setSeverity("info");
        setOpen(true);
      } else {
        setAlertMessage("Something went wrong.");
        setSeverity("error");
        setOpen(true);
      }
    } catch (error) {
      console.error(error);
      const message =
        error.response?.data?.message || "Something went wrong during reset.";
      setAlertMessage(message);
      setSeverity("error");
      setOpen(true);
    } finally {
      setButtonText("Set Password");
    }
  };

  // Single requirement item
  const RequirementItem = ({ met, text }) => (
    <div className="flex items-center gap-2 transition-all duration-300">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
          met ? "bg-green-500 scale-110" : "bg-gray-200"
        }`}
      >
        {met ? (
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        ) : (
          <X className="w-3 h-3 text-gray-400" strokeWidth={2} />
        )}
      </div>
      <span
        className={`text-sm transition-colors duration-300 ${
          met ? "text-green-700 font-medium" : "text-gray-500"
        }`}
      >
        {text}
      </span>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-8 text-white">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2.5">
                <Shield className="w-7 h-7" />
              </div>
              <div className="text-center">
                <h1 className="text-2xl font-bold">C-HIM</h1>
                <p className="text-xs text-blue-100 mt-0.5">
                  Center for Hypertension & Internal Medicine
                </p>
              </div>
            </div>
            <h2 className="text-center text-lg font-medium text-blue-50">
              Reset Your Password
            </h2>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-8">
            <div className="space-y-5 mb-6 text-start">
              {/* New Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Strength */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-700">
                  Password Strength
                </span>
                <span className="text-xs font-medium text-gray-500">
                  {metCount} of {requirements.length} requirements met
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ease-out ${
                    progressPercentage === 100
                      ? "bg-green-500"
                      : progressPercentage >= 66
                      ? "bg-blue-500"
                      : progressPercentage >= 33
                      ? "bg-yellow-500"
                      : "bg-red-400"
                  }`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {/* Requirement List */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-6 mb-6 border border-gray-100">
              <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                Password Requirements
              </h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {requirements.map((req, index) => (
                  <RequirementItem
                    key={index}
                    met={req.met}
                    text={req.text}
                  />
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!allRequirementsMet}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                allRequirementsMet
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {!allRequirementsMet?"Complete All Requirements":(buttonText)}
            </button>
          </form>
        </div>
      </div>

      <ToastMessage
        message={alertmessage}
        severity={severity}
        open={open}
        handleClose={handlealert}
      />
    </>
  );
}
